/**
 * Safe localStorage helpers for onboarding flags.
 *
 * Guards against:
 * - Missing localStorage (SSR / privacy mode) - every read/write is wrapped
 * - Corrupt or non-"true" values - coerced to boolean false
 * - Stale flags from other users - `sweepOtherUsers()` removes them on
 *   login or sign-out so progress can't bleed across accounts
 * - Indefinite staleness - flags carry a timestamp and expire after TTL
 */

const VISITED_BROWSE_PREFIX = "focus_visited_browse_";
const DISMISSED_PREFIX = "focus_client_onboarding_dismissed_";
const AGENCY_DISMISSED_PREFIX = "focus_agency_onboarding_dismissed_";

// 90 days - long enough to remember within a project cycle, short enough
// that a flag set on a shared device eventually clears itself.
const FLAG_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const ALL_PREFIXES = [VISITED_BROWSE_PREFIX, DISMISSED_PREFIX, AGENCY_DISMISSED_PREFIX];

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readFlag(key: string, options?: { permanent?: boolean }): boolean {
  const ls = safeStorage();
  if (!ls) return false;
  try {
    const raw = ls.getItem(key);
    if (!raw) return false;

    // Legacy plain-string format ("true"). Treat as fresh and migrate.
    if (raw === "true") {
      writeFlag(key, true);
      return true;
    }

    const parsed = JSON.parse(raw) as { v?: boolean; t?: number } | null;
    if (!parsed || parsed.v !== true || typeof parsed.t !== "number") {
      ls.removeItem(key);
      return false;
    }
    // User-issued dismissals must not silently expire. Only advisory flags
    // (e.g. "has visited browse") use the TTL.
    if (!options?.permanent && Date.now() - parsed.t > FLAG_TTL_MS) {
      ls.removeItem(key);
      return false;
    }
    return true;
  } catch {
    // Corrupt JSON or thrown getItem - drop the value.
    try { ls.removeItem(key); } catch { /* noop */ }
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    if (!value) {
      ls.removeItem(key);
      return;
    }
    ls.setItem(key, JSON.stringify({ v: true, t: Date.now() }));
  } catch {
    /* quota / privacy mode - ignore */
  }
}

export function hasVisitedBrowse(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return readFlag(`${VISITED_BROWSE_PREFIX}${userId}`);
}

export function markVisitedBrowse(userId: string | null | undefined): void {
  if (!userId) return;
  writeFlag(`${VISITED_BROWSE_PREFIX}${userId}`, true);
}

export function isChecklistDismissed(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return readFlag(`${DISMISSED_PREFIX}${userId}`, { permanent: true });
}

export function dismissChecklist(userId: string | null | undefined): void {
  if (!userId) return;
  writeFlag(`${DISMISSED_PREFIX}${userId}`, true);
}

export function isAgencyChecklistDismissed(userId: string | null | undefined): boolean {
  if (!userId) return false;
  return readFlag(`${AGENCY_DISMISSED_PREFIX}${userId}`, { permanent: true });
}

export function dismissAgencyChecklist(userId: string | null | undefined): void {
  if (!userId) return;
  writeFlag(`${AGENCY_DISMISSED_PREFIX}${userId}`, true);
}

export function restoreAgencyChecklist(userId: string | null | undefined): void {
  if (!userId) return;
  writeFlag(`${AGENCY_DISMISSED_PREFIX}${userId}`, false);
}

/**
 * Remove onboarding flags belonging to any user other than `currentUserId`.
 * Called on login (account switch) and on sign-out so the next person on the
 * device starts from a clean slate. Pass `null` to clear ALL onboarding flags.
 */
export function sweepOtherUsers(currentUserId: string | null): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (!key) continue;
      const prefix = ALL_PREFIXES.find((p) => key.startsWith(p));
      if (!prefix) continue;
      const owner = key.slice(prefix.length);
      if (currentUserId && owner === currentUserId) continue;
      toRemove.push(key);
    }
    for (const k of toRemove) {
      try { ls.removeItem(k); } catch { /* noop */ }
    }
  } catch {
    /* noop */
  }
}
